/** Bright Acorns poke mood: 5 happy taps / 10s, then any tap is annoyed (window refreshes). */
export const HAPPY_POKE_CAP = 5;
export const POKE_WINDOW_MS = 10_000;
export const ANNOYED_MS = 1400;

export const FUN_POKE_GLYPHS = ['❤️', '✨', '💛', '🌸', '⭐', '🌟', '🎵', '💫', '💚'] as const;
export const ANNOYED_POKE_GLYPHS = ['💢', '😤', '😒', '💨', '😑', '💤'] as const;

export type PokeSfx = 'petPoke' | 'petPlay' | 'petSticker' | 'petYum' | 'petAnnoyed';
export type PokeCss =
  | 'habitat-poke'
  | 'habitat-poke-wiggle'
  | 'habitat-poke-spin'
  | 'habitat-poke-hop'
  | 'habitat-poke-tip'
  | 'habitat-poke-zoom'
  | 'habitat-poke-annoyed';

export type PokeAction = {
  css: PokeCss;
  sfx: PokeSfx;
  ms: number;
  bursts: number;
  glyphs: readonly string[];
  annoyed: boolean;
};

export type PokeMood = {
  taps: number;
  windowAt: number;
  annoyedUntil: number;
  lastCss: string;
};

export type PokeBurst = {
  id: number;
  glyph: string;
  kind: 'burst' | 'react';
  rx: string;
  ry: string;
  delay: string;
};

export const EMPTY_POKE_MOOD: PokeMood = { taps: 0, windowAt: 0, annoyedUntil: 0, lastCss: '' };

export const FUN_POKE_ACTIONS: Omit<PokeAction, 'annoyed' | 'glyphs'>[] = [
  { css: 'habitat-poke', sfx: 'petPoke', ms: 900, bursts: 1 },
  { css: 'habitat-poke-wiggle', sfx: 'petPoke', ms: 900, bursts: 1 },
  { css: 'habitat-poke-spin', sfx: 'petPlay', ms: 1150, bursts: 2 },
  { css: 'habitat-poke-hop', sfx: 'petPlay', ms: 1000, bursts: 3 },
  { css: 'habitat-poke-tip', sfx: 'petSticker', ms: 950, bursts: 1 },
  { css: 'habitat-poke-zoom', sfx: 'petYum', ms: 950, bursts: 2 },
];

const ANNOYED_ACTION: PokeAction = {
  css: 'habitat-poke-annoyed',
  sfx: 'petAnnoyed',
  ms: ANNOYED_MS,
  bursts: 3,
  glyphs: ANNOYED_POKE_GLYPHS,
  annoyed: true,
};

export function notePoke(mood: PokeMood, now: number): { next: PokeMood; annoyed: boolean } {
  if (mood.annoyedUntil > now) {
    return {
      next: { ...mood, annoyedUntil: now + POKE_WINDOW_MS },
      annoyed: true,
    };
  }
  const windowFresh = mood.windowAt === 0 || now - mood.windowAt > POKE_WINDOW_MS;
  const taps = windowFresh ? 1 : mood.taps + 1;
  const windowAt = windowFresh ? now : mood.windowAt;
  if (taps > HAPPY_POKE_CAP) {
    return {
      next: { taps, windowAt, annoyedUntil: now + POKE_WINDOW_MS, lastCss: mood.lastCss },
      annoyed: true,
    };
  }
  return { next: { taps, windowAt, annoyedUntil: 0, lastCss: mood.lastCss }, annoyed: false };
}

export function pickPokeAction(
  mood: PokeMood,
  annoyed: boolean,
  rng: () => number = Math.random,
): PokeAction {
  if (annoyed) return ANNOYED_ACTION;
  const pool = FUN_POKE_ACTIONS.filter((a) => a.css !== mood.lastCss);
  const choices = pool.length ? pool : FUN_POKE_ACTIONS;
  const picked = choices[Math.floor(rng() * choices.length)] ?? FUN_POKE_ACTIONS[0];
  return { ...picked, glyphs: FUN_POKE_GLYPHS, annoyed: false };
}

export function makePokeBursts(action: PokeAction, rng: () => number = Math.random): PokeBurst[] {
  const count = action.bursts;
  return Array.from({ length: count }, (_, i) => {
    const angle = (-55 + (110 / Math.max(1, count - 1)) * i + (rng() * 14 - 7)) * (Math.PI / 180);
    const dist = 1.6 + rng() * 1.1;
    return {
      id: i,
      glyph: action.glyphs[Math.floor(rng() * action.glyphs.length)] ?? action.glyphs[0],
      kind: count > 1 ? 'react' : 'burst',
      rx: `${Math.cos(angle) * dist}em`,
      ry: `${-1 - Math.sin(Math.abs(angle)) * dist}em`,
      delay: `${i * 0.055}s`,
    };
  });
}
